import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Typography } from '../../../../components/common/Typography/Typography';
import {
  ModCategory,
  ModCategoryType,
  ModSortBy,
  ModSortByType,
} from '../../../../constants/enums';
import { useDocumentHead } from '../../../../hooks/useDocumentHead/useDocumentHead';
import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { toCssUrl } from '../../../../services/urlSafety';
import { useGetModsQuery } from '../../api/modsApi';
import { ModCard } from '../../components/ModCard/ModCard';
import { ModSearchBar } from '../../components/ModSearchBar/ModSearchBar';
import { Mod } from '../../types/modTypes';
import { getThumbnailSrc } from '../../utils/mediaUtils';
import { sortMods } from '../../utils/modSortUtils';
import styles from './ModListScreen.module.css';
import { ModListScreenProps } from './ModListScreen.types';

export const ModListScreen: React.FC<ModListScreenProps> = ({
  category: initialCategory,
  onCategoryChange,
  sortBy: initialSortBy = ModSortBy.UPDATED_AT,
  onSortChange,
  searchQuery: initialSearchQuery,
  onSearchChange,
  className = '',
}) => {
  const { t } = useTranslation();

  const [prevCategory, setPrevCategory] = useState(initialCategory);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);

  const [prevSortBy, setPrevSortBy] = useState(initialSortBy);
  const [selectedSortBy, setSelectedSortBy] = useState(initialSortBy);

  const [prevSearchQuery, setPrevSearchQuery] = useState(initialSearchQuery);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');

  const [displayedBg, setDisplayedBg] = useState<string>('');
  const [incomingBg, setIncomingBg] = useState<string | null>(null);

  if (initialCategory !== prevCategory) {
    setPrevCategory(initialCategory);
    setSelectedCategory(initialCategory);
    setDisplayedBg('');
    setIncomingBg(null);
  }

  if (initialSortBy !== prevSortBy) {
    setPrevSortBy(initialSortBy);
    setSelectedSortBy(initialSortBy);
  }

  if (initialSearchQuery !== prevSearchQuery) {
    setPrevSearchQuery(initialSearchQuery);
    setSearchQuery(initialSearchQuery || '');
  }

  const categoryTitle =
    selectedCategory === ModCategory.UEVR
      ? t.nav.uevrMods
      : selectedCategory === ModCategory.VR
        ? t.nav.vrMods
        : selectedCategory === ModCategory.HEAD_TRACKING
          ? t.nav.headTrackingMods
          : undefined;

  const docTitle = categoryTitle
    ? `${categoryTitle} - ${t.brand}`
    : `${t.brand} - ${t.seo.homeTitle}`;

  useDocumentHead({
    title: docTitle,
    description: t.seo.homeDescription,
  });

  const {
    data: mods,
    isLoading,
    error,
  } = useGetModsQuery({
    category: selectedCategory,
    sortBy: selectedSortBy,
  });

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (onSearchChange) {
        onSearchChange(query);
      }
    },
    [onSearchChange]
  );

  const sortedMods = useMemo(() => {
    if (!mods) return [];
    const sorted = sortMods(mods, selectedSortBy);
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return sorted;
    }
    return sorted.filter((mod) => {
      const titleMatch = mod.title.toLowerCase().includes(query);
      const gameMatch = mod.gameTitle?.toLowerCase().includes(query);
      const authorMatch = (mod.modAuthor || mod.author)?.toLowerCase().includes(query);
      const descMatch = mod.description?.toLowerCase().includes(query);
      const tagMatch = mod.tags?.some((tag) => tag.toLowerCase().includes(query));
      return Boolean(titleMatch || gameMatch || authorMatch || descMatch || tagMatch);
    });
  }, [mods, selectedSortBy, searchQuery]);

  let defaultThumbnail = '';
  if (sortedMods && sortedMods.length > 0) {
    for (let i = sortedMods.length - 1; i >= 0; i--) {
      const mod = sortedMods[i];
      if (mod?.thumbnailUrl && mod.thumbnailUrl.trim()) {
        defaultThumbnail = getThumbnailSrc(mod.thumbnailUrl);
        break;
      }
    }
  }

  const currentBg = displayedBg || defaultThumbnail;

  const handleAnimationEnd = useCallback((bg: string) => {
    setDisplayedBg(bg);
    setIncomingBg(null);
  }, []);

  useEffect(() => {
    if (!incomingBg) return;
    const timer = setTimeout(() => {
      setDisplayedBg(incomingBg);
      setIncomingBg(null);
    }, 500);
    return () => clearTimeout(timer);
  }, [incomingBg]);

  const handleModHover = useCallback(
    (mod: Mod) => {
      if (!mod.thumbnailUrl || !mod.thumbnailUrl.trim()) {
        return;
      }
      const thumbUrl = getThumbnailSrc(mod.thumbnailUrl);
      if (!thumbUrl) return;

      if (thumbUrl === currentBg || thumbUrl === incomingBg) {
        return;
      }

      setIncomingBg(thumbUrl);
    },
    [currentBg, incomingBg]
  );

  const handleFilterClick = (clickedCategory: ModCategoryType) => {
    const nextCategory = selectedCategory === clickedCategory ? undefined : clickedCategory;
    setSelectedCategory(nextCategory);
    setDisplayedBg('');
    setIncomingBg(null);

    if (onCategoryChange) {
      onCategoryChange(nextCategory);
    }
  };

  const handleSortClick = (newSortBy: ModSortByType) => {
    setSelectedSortBy(newSortBy);
    if (onSortChange) {
      onSortChange(newSortBy);
    }
  };

  const pageTitle =
    selectedCategory === ModCategory.UEVR
      ? t.nav.uevrMods
      : selectedCategory === ModCategory.VR
        ? t.nav.vrMods
        : selectedCategory === ModCategory.HEAD_TRACKING
          ? t.nav.headTrackingMods
          : t.mods.featuredTitle;

  return (
    <div className={styles.modsWrapper}>
      {currentBg && (
        <div
          className={styles.pageBlurredBackdrop}
          style={{ backgroundImage: toCssUrl(currentBg) }}
          aria-hidden="true"
          data-testid="page-blurred-backdrop-current"
        />
      )}
      {incomingBg && (
        <div
          key={incomingBg}
          className={`${styles.pageBlurredBackdrop} ${styles.fadeIn}`}
          style={{ backgroundImage: toCssUrl(incomingBg) }}
          onAnimationEnd={() => handleAnimationEnd(incomingBg)}
          aria-hidden="true"
          data-testid="page-blurred-backdrop-incoming"
        />
      )}
      {(currentBg || incomingBg) && (
        <div
          className={styles.pageBackdropOverlay}
          aria-hidden="true"
          data-testid="page-backdrop-overlay"
        />
      )}

      <div className={`${styles.container} ${className}`.trim()}>
        <div className={styles.header}>
          <Typography variant="h1">{pageTitle}</Typography>
          <div className={styles.controlsRow}>
            <div className={styles.filterGroup} role="group" aria-label={t.mods.categoryFilters}>
              <button
                type="button"
                className={`${styles.filterBtn} ${
                  selectedCategory === ModCategory.VR ? styles.filterBtnActive : ''
                }`}
                onClick={() => handleFilterClick(ModCategory.VR)}
                aria-pressed={selectedCategory === ModCategory.VR}
              >
                {t.nav.vrMods}
              </button>
              <button
                type="button"
                className={`${styles.filterBtn} ${
                  selectedCategory === ModCategory.UEVR ? styles.filterBtnActive : ''
                }`}
                onClick={() => handleFilterClick(ModCategory.UEVR)}
                aria-pressed={selectedCategory === ModCategory.UEVR}
              >
                {t.nav.uevrMods}
              </button>
              <button
                type="button"
                className={`${styles.filterBtn} ${
                  selectedCategory === ModCategory.HEAD_TRACKING ? styles.filterBtnActive : ''
                }`}
                onClick={() => handleFilterClick(ModCategory.HEAD_TRACKING)}
                aria-pressed={selectedCategory === ModCategory.HEAD_TRACKING}
              >
                {t.nav.headTrackingMods}
              </button>
            </div>

            <div className={styles.searchWrapper}>
              <ModSearchBar value={searchQuery} onChange={handleSearchChange} />
            </div>

            <div className={styles.sortGroup} role="group" aria-label={t.mods.sortByLabel}>
              <span className={styles.sortLabel}>{t.mods.sortByLabel}:</span>
              <button
                type="button"
                className={`${styles.sortBtn} ${
                  selectedSortBy === ModSortBy.UPDATED_AT ? styles.sortBtnActive : ''
                }`}
                onClick={() => handleSortClick(ModSortBy.UPDATED_AT)}
                aria-pressed={selectedSortBy === ModSortBy.UPDATED_AT}
              >
                {t.mods.sortByUpdated}
              </button>
              <button
                type="button"
                className={`${styles.sortBtn} ${
                  selectedSortBy === ModSortBy.CREATED_AT ? styles.sortBtnActive : ''
                }`}
                onClick={() => handleSortClick(ModSortBy.CREATED_AT)}
                aria-pressed={selectedSortBy === ModSortBy.CREATED_AT}
              >
                {t.mods.sortByCreated}
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className={styles.gridContainer}>
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={styles.skeletonCard} />
            ))}
          </div>
        ) : error || !mods ? (
          <div className={styles.messageBox}>
            <Typography variant="body1">{t.mods.loadError}</Typography>
          </div>
        ) : sortedMods.length === 0 ? (
          <div className={styles.messageBox}>
            <Typography variant="subtitle1">
              {searchQuery.trim()
                ? t.mods.noSearchResults({ query: searchQuery.trim() })
                : t.mods.noResults}
            </Typography>
            {searchQuery.trim() && (
              <button
                type="button"
                className={styles.clearSearchFilterBtn}
                onClick={() => handleSearchChange('')}
              >
                {t.mods.clearSearch}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.gridContainer}>
            {sortedMods.map((mod) => (
              <ModCard key={mod.id} mod={mod} onMouseEnter={handleModHover} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
